import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, Spin, WarningCard } from 'common/components';
import { showMessage, showSuccessMessage } from 'common/helpers';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { renderStockListSettingsRecords } from 'entities/Settings/Settings.helper';
import { StockCreateCard } from 'entities/Stock/components';
import { IUpdateStockPayload } from 'entities/Stock/Stock.models';
import {
  createStock,
  deleteStock,
  getStockById,
  getStockList,
  selectIsStockLoading,
  selectStock,
  selectStockList,
  setStock,
  setStockList,
  updateStock,
} from 'entities/Stock/Stock.slice';

interface IComponentProps {
  header: ReactNode;
}

export const SettingsAccountStockListTab: FC<IComponentProps> = ({ header }) => {
  const dispatch = useAppDispatch();

  const stock = useAppSelector(selectStock);
  const stockList = useAppSelector(selectStockList);
  const isStockLoading = useAppSelector(selectIsStockLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openStockCreateCard, setOpenStockCreateCard] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);

  useEffect(() => {
    const fetch = async () => {
      dispatch(setStockList([]));
      await dispatch(getStockList({ limit: LIST_LIMIT_0 }));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  const handleOpenStockCreateCard = async (id?: number): Promise<void> => {
    if (id) {
      await dispatch(getStockById({ id }));
    }

    setOpenStockCreateCard(true);
  };

  const closeStockCreateCard = (): void => {
    setOpenStockCreateCard(false);
    dispatch(setStock(null));
  };

  const handleCreateStock = ({ name, address, cityId, onSuccess }: Omit<IUpdateStockPayload, 'id'>): void => {
    dispatch(
      createStock({
        name: name as string,
        displayName: name as string,
        address: (address as string).replaceAll('\n', ' '),
        cityId: cityId as number,
        onSuccess: () => {
          showSuccessMessage('Новый склад добавлен.');
          onSuccess?.();
        },
      }),
    );
  };

  const handleUpdateStock = (payload: Omit<IUpdateStockPayload, 'id'>): void => {
    if (!stock) {
      return;
    }

    dispatch(
      updateStock({
        ...payload,
        id: stock.id,
        address: payload.address?.replaceAll('\n', ' '),
        onSuccess: () => {
          showSuccessMessage('Склад успешно изменен.');
          payload.onSuccess?.();
          dispatch(setStock(null));
        },
      }),
    );
  };

  const handleOpenWarningCard = (id: number): void => {
    dispatch(
      getStockById({
        id,
        onSuccess: () => setOpenWarningCard(true),
      }),
    );
  };

  const closeWarningCard = (): void => {
    setOpenWarningCard(false);
    dispatch(setStock(null));
  };

  const handleDeleteStock = (): void => {
    if (!stock) {
      return;
    }

    dispatch(
      deleteStock({
        id: stock.id,
        onSuccess: () => {
          showMessage({
            content: `Склад «${stock.name}» удален.`,
            icon: <Bin className="icon-bin-dark-grey" />,
          });
          setOpenWarningCard(false);
          dispatch(setStock(null));
        },
      }),
    );
  };

  return (
    <>
      <InfiniteScroll>
        {header}

        <div className="mb-6">
          <Button
            className="button-sm primary"
            icon={<Plus className="icon-plus-white" />}
            onClick={() => handleOpenStockCreateCard()}
          >
            Добавить склад
          </Button>
        </div>

        <Table
          className={`${!stockList.length && 'table-empty'} mb-19 account-stock-list-settings__table`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={stockList.map((stock) => ({ ...stock, key: stock.id }))}
          columns={renderStockListSettingsRecords(handleOpenStockCreateCard, handleOpenWarningCard)}
          loading={isStockLoading}
          locale={{
            emptyText: fetchLoading ? (
              <Spin />
            ) : (
              <div className="text-body color-dark-grey">Склады отгрузки ещё не добавлены.</div>
            ),
          }}
        />
      </InfiniteScroll>

      <StockCreateCard
        open={openStockCreateCard}
        loading={isStockLoading}
        stock={stock}
        onCancel={closeStockCreateCard}
        onSubmit={stock ? handleUpdateStock : handleCreateStock}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить адрес доставки?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить"
        loading={isStockLoading}
        onConfirm={handleDeleteStock}
        onClose={closeWarningCard}
      />
    </>
  );
};
